import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Output, Input } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { SelectableOption } from '../../base-types';

/**
 * Modello interno. Uso esclusivamente privato
 */
class FlatNode {
  id: number | string;  // non serve al CDK. Serve solo al consumer per matchare gli elementi selezionati con il modello esterno
  label: string;        // viene visualizzata dalla view
  level: number;
  hasChildren: boolean;
}

/**
 * @title Tree with checkboxes
 */
@Component({
  selector: 'app-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  styleUrls: ['./checkbox-tree.component.scss']
})
export class CheckboxTreeComponent {

  @Output() selectionChange: EventEmitter<(number|string)[]> = new EventEmitter<(number|string)[]>();

  @Input() set selectableOptions(selectableOptions: SelectableOption[]) {
    console.log('CheckboxTreeComponent - set selectableOptions :', JSON.stringify(selectableOptions));
    if(selectableOptions)
      this.dataSource.data = selectableOptions;
  }

  @Input() set selection(value: (string|number)[]){
    console.log('CheckboxTreeComponent - set selection :', JSON.stringify(value));
    if(this.treeControl.dataNodes) {
      for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
        //Per prima cosa deseleziona il nodo, nel caso stesse operando
        //su un tree precedentemente valorizzato
        this.selectionModel.deselect(this.treeControl.dataNodes[i])
        if (value && value.includes(this.treeControl.dataNodes[i].id)) {
          this.selectionModel.toggle(this.treeControl.dataNodes[i])
          //this.treeControl.expand(this.treeControl.dataNodes[i]) 
          //non funziona il comando expand()
          // https://stackoverflow.com/questions/53545096/how-do-i-programmatically-select-material-tree-nodes-with-selectionmodel
          // https://stackblitz.com/edit/angular-j2nf2r?embed=1&file=app/tree-checklist-example.html
        }
      }
    }
    this.emit()
  }
  
  public treeFlattener: MatTreeFlattener<SelectableOption, FlatNode>;
  public dataSource: MatTreeFlatDataSource<SelectableOption, FlatNode>;
  /**
   * Serve per gestire lo stato di expanded/collapsed dei nodi dell'albero
   */
  public treeControl: FlatTreeControl<FlatNode>;
  /**
   * Serve per gestire lo stato di selezione delle checkbox
   */
  public selectionModel = new SelectionModel<FlatNode>(true /* multiple */);

  constructor() {
    // Tutti questi oggetti di material agiscono su oggetti custom, in questo caso SelectableOption e FlatNode
    // Hanno quindi bisogno di sapere come ricavare il livello e se il nodo ha dei figli. Quindi richiedono in 
    // input le funzioni necessarie per ricavare tali informazioni a partire dagli oggetti custom
    this.treeControl = new FlatTreeControl<FlatNode>(
      (el: FlatNode) => el.level,             //vuole in input l'implementazione della funzione per ricavare il livello
      (el: FlatNode) => el.hasChildren        //vuole in input l'implementazione della funzione per sapere se il nodo ha figli
    );
    this.treeFlattener = new MatTreeFlattener<SelectableOption, FlatNode>(
      this.transformer, 
      (el: FlatNode) => el.level,             //vuole in input l'implementazione della funzione per ricavare il livello
      (el: FlatNode) => el.hasChildren,       //vuole in input l'implementazione della funzione per sapere se il nodo ha figli
      (el: SelectableOption): SelectableOption[] => el.children //vuole in input l'implementazione della funzione per ottenere i figli del nodo
    );
    this.dataSource = new MatTreeFlatDataSource<SelectableOption, FlatNode>(
      this.treeControl, 
      this.treeFlattener
    );
  }

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  private flatNodeToSelectableOptionMap = new Map<FlatNode, SelectableOption>();
  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  private selectableOptionToFlatNodeMap = new Map<SelectableOption, FlatNode>();
  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: SelectableOption, level: number): FlatNode => {
    const existingNode: FlatNode = this.selectableOptionToFlatNodeMap.get(node);
    const flatNode = existingNode && existingNode.id === node.id
        ? existingNode
        : new FlatNode();
    flatNode.id = node.id;
    flatNode.label = node.label;
    flatNode.level = level;
    flatNode.hasChildren = !!node.children; // equivale a "node.children != null"
    this.flatNodeToSelectableOptionMap.set(flatNode, node);
    this.selectableOptionToFlatNodeMap.set(node, flatNode);
    return flatNode;
  }

  /**
   * funzione usata dalla direttiva
   * <pre>
   *  <code>
   *    *matTreeNodeDef when:
   *  </code>
   * </pre>
   */
  hasChild = (_: number, _nodeData: FlatNode) => _nodeData.hasChildren;

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child =>
      this.selectionModel.isSelected(child)
    );
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.selectionModel.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle a node selection. Select/deselect all the descendants node */
  toggleItemSelection(node: FlatNode): void {
    this.selectionModel.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.selectionModel.isSelected(node)
      ? this.selectionModel.select(...descendants)
      : this.selectionModel.deselect(...descendants);

    // Force update for the parent
    descendants.every(child =>
      this.selectionModel.isSelected(child)
    );
    this._checkAllParentsSelection(node);

    this.emit()
  }

  /** Toggle a leaf node selection. Check all the parents to see if they changed */
  toggleLeafItemSelection(node: FlatNode): void {
    this.selectionModel.toggle(node);
    this._checkAllParentsSelection(node);

    this.emit()
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  _checkAllParentsSelection(node: FlatNode): void {
    let parent: FlatNode | null = this._getParentNode(node);
    while (parent) {
      this._checkRootNodeSelection(parent);
      parent = this._getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  _checkRootNodeSelection(node: FlatNode): void {
    const nodeSelected = this.selectionModel.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child =>
      this.selectionModel.isSelected(child)
    );
    if (nodeSelected && !descAllSelected) {
      this.selectionModel.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.selectionModel.select(node);
    }
  }

  /* Get the parent node of a node */
  _getParentNode(node: FlatNode): FlatNode | null {
    const currentLevel = node.level;

    if (currentLevel < 1)
      return null;

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentNode.level < currentLevel)
        return currentNode;
    }
    return null;
  }

  // Eventuali nodi senza id selezionati vengono esclusi dal risultato perchè si tratta di semplici categorie, che non sono 
  // effettivamente selezionabili se non per selezionare le foglie
  emit = () => this.selectionChange.emit(this.selectionModel.selected.map(el=>el.id).filter(el=>el!=null))
}
